import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { T, useTranslation } from "@redotech/redo-i18n";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  RETURNS_SETTINGS_TAB_BY_LEGACY_PATH,
  RETURNS_SETTINGS_TAB_QUERY_PARAM,
} from "@redotech/redo-merchant-app-common/settings/returns-settings-tab";
import {
  ReturnsSettingsExperience,
  useReturnsSettingsExperience,
} from "@redotech/redo-merchant-app-common/settings/use-returns-settings-experience";
import { RETURNS_CONSOLIDATED_SETTINGS_PATH } from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { PlatformEvents } from "../../../platform-events";
import { formatOldSettingsShutoffDate } from "../../../setting/returns/switch-back/old-settings-shutoff";
import { SwitchBackModal } from "../../../setting/returns/switch-back/switch-back-modal";
import { useSwitchToLegacySettings } from "../../../setting/returns/switch-back/use-switch-to-legacy-settings";
import * as css from "./returns-settings-experience-banner.module.css";

const DISMISSED_STORAGE_KEY =
  "redo.returns-settings.switch-back-banner-dismissed";

/** Offers the way back to the old pages on the new one, and the way forward on the old ones. */
export const ReturnsSettingsExperienceBanner = memo(
  function ReturnsSettingsExperienceBanner() {
    const experience = useReturnsSettingsExperience();
    if (experience.pending || !experience.canSwitch) {
      return null;
    }
    return experience.useNewUi ? (
      <SwitchBackBanner />
    ) : (
      <SwitchToNewBanner experience={experience} />
    );
  },
);

const SwitchBackBanner = memo(function SwitchBackBanner() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useLocalStorage(
    DISMISSED_STORAGE_KEY,
    false,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const switchToLegacy = useSwitchToLegacySettings();

  const switchBack = useHandler(() => {
    setModalOpen(false);
    switchToLegacy();
  });

  const dismiss = useHandler(() => {
    setDismissed(true);
    amplitude.track(
      PlatformEvents.RETURNS_SETTINGS_SWITCH_BACK_BANNER_DISMISSED,
    );
  });

  if (
    dismissed ||
    pathname !== routes.getSettingsSubRoute(RETURNS_CONSOLIDATED_SETTINGS_PATH)
  ) {
    return null;
  }

  return (
    <>
      <BannerShell
        action={
          <RedoButton
            aria-label={t("Dismiss", { context: "notification banner" })}
            IconLeading={CloseIcon}
            onClick={dismiss}
            size="sm"
          />
        }
      >
        <T
          components={{
            ...emphasis,
            cta: (
              <RedoButton
                buttonClassName={css.inlineLinkButton}
                className={css.inlineLink}
                dataTrackId="merchant.settings.returns.switch-back.banner-link"
                hierarchy="link_underline"
                onClick={() => setModalOpen(true)}
                size="xl"
                text={t("switch back", {
                  context: "link to return to the old returns settings",
                })}
                textWeight="regular"
              />
            ),
          }}
          i18nKey="<lead>We've updated returns settings.</lead> You can <cta/> to the previous experience for now. This update becomes permanent on <bold>{{date}}</bold>."
          values={{ date: formatOldSettingsShutoffDate() }}
        />
      </BannerShell>
      <SwitchBackModal
        onCancel={() => setModalOpen(false)}
        onConfirm={switchBack}
        open={modalOpen}
      />
    </>
  );
});

/**
 * Not dismissible: after switching back it is the only way to the new page,
 * which merchants return to once they've reported what it was missing.
 */
const SwitchToNewBanner = memo(function SwitchToNewBanner({
  experience,
}: {
  experience: ReturnsSettingsExperience;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const tab = Object.entries(RETURNS_SETTINGS_TAB_BY_LEGACY_PATH).find(
    ([legacyPath]) => pathname === routes.getSettingsSubRoute(legacyPath),
  )?.[1];

  const switchToNew = useHandler(() => {
    if (!tab) {
      return;
    }
    experience.switchToNew();
    navigate(
      `${routes.getSettingsSubRoute(RETURNS_CONSOLIDATED_SETTINGS_PATH)}?${new URLSearchParams({ [RETURNS_SETTINGS_TAB_QUERY_PARAM]: tab })}`,
    );
  });

  if (!tab) {
    return null;
  }

  return (
    <BannerShell
      action={
        <RedoButton
          dataTrackId="merchant.settings.returns.switch-to-new.banner-button"
          hierarchy="secondary"
          onClick={switchToNew}
          size="sm"
          text={t("Switch to new settings")}
        />
      }
    >
      <T
        components={emphasis}
        i18nKey="<lead>You're using the previous Returns settings.</lead> You can switch to the new experience anytime. It becomes permanent on <bold>{{date}}</bold>."
        values={{ date: formatOldSettingsShutoffDate() }}
      />
    </BannerShell>
  );
});

const emphasis = {
  lead: (
    <Text as="span" fontSize="md" fontWeight="semibold" textColor="primary">
      {null}
    </Text>
  ),
  bold: (
    <Text as="span" fontSize="md" fontWeight="bold" textColor="primary">
      {null}
    </Text>
  ),
};

function BannerShell({
  children,
  action,
}: {
  children: ReactNode;
  action: ReactNode;
}) {
  return (
    <Flex
      align="center"
      bgColor="primary"
      borderBottomWidth="1px"
      borderColor="primary"
      borderStyle="solid"
      gap="lg"
      px="3xl"
      py="lg"
      w="full"
    >
      <RedoFeaturedIcon color="gray" Icon={AlertCircleIcon} />
      <Flex grow={1}>
        <Text fontSize="md" textColor="secondary">
          {children}
        </Text>
      </Flex>
      {action}
    </Flex>
  );
}
